import { useEffect, useRef, useState } from "react";

export function useMediaQuery(query) {
	const [matches, setMatches] = useState(false);

	useEffect(() => {
		const media = window.matchMedia(query);
		if (media.matches !== matches) {
			setMatches(media.matches);
		}
		const listener = () => {
			setMatches(media.matches);
		};
		try {
			media.addEventListener("change", listener);
		} catch (e) {
			// Safari isn't supporting mediaQueryList.addEventListener
			media.addListener(listener);
		}
		return () => {
			try {
				media.removeEventListener("change", listener);
			} catch (e) {
				// Safari isn't supporting mediaQueryList.addEventListener
				media.removeListener(listener);
			}
		};
	}, [matches, query]);

	return matches;
}

export function useDebounce(query, fetchFunction, time) {
	const [timer, setTimer] = useState(null);
	const [fetching, setFetching] = useState(false);
	const [typing, setTyping] = useState(false);
	useEffect(() => {
		if (query && query != "") {
			setTyping(true);
			setFetching(false);
			clearTimeout(timer);
			setTimer(
				setTimeout(() => {
					setTyping(false);
					setFetching(true);
					fetchFunction(query);
					setTimeout(() => {
						setFetching(false);
					}, 300);
				}, time)
			);
		}

		return () => {
			clearTimeout(timer);
		};
	}, [query]);
	return [fetching, typing];
}

export function useStateCallback(initialState) {
	const [state, setState] = useState(initialState);
	const cbRef = useRef(null);

	const setStateCallback = (state, cb) => {
		cbRef.current = cb;
		setState(state);
	};

	useEffect(() => {
		if (cbRef.current) {
			cbRef.current(state);
			cbRef.current = null;
		}
	}, [state]);

	return [state, setStateCallback];
}

export const useImportIcon = (id, importFn, mountControl) => {
	const [icon, setIcon] = useState(null);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		let mount = mountControl ? mountControl : true;
		if (id && mount) {
			importFn
				.then((res) => {
					setIcon(res.default);
					setLoaded(!loaded);
				})
				.catch(() => {
					setIcon(null);
					setLoaded(false);
				});
		}
		return () => {
			mount = false;
		};
	}, [id]);

	return [icon, setIcon, loaded];
};

export const useProgressiveImage = (src) => {
	const [sourceLoaded, setSourceLoaded] = useState(null);

	useEffect(() => {
		const img = new Image();
		img.src = src;
		img.onload = () => setSourceLoaded(src);
	}, [src]);

	return sourceLoaded;
};

export function useStrongPasswordValidator(t) {
	if (!window.SIGNUP_STRONG_PASSWORD_VALIDATOR) {
		return undefined;
	}
	return (_, value) => {
		if (!value) return Promise.resolve();
		const minLength = 8;
		const hasUpper = /[A-Z]/.test(value);
		const hasLower = /[a-z]/.test(value);
		const hasDigit = /[0-9]/.test(value);
		const hasSpecial = /[^A-Za-z0-9]/.test(value);
		if (value.length >= minLength && hasUpper && hasLower && hasDigit && hasSpecial) {
			return Promise.resolve();
		}
		return Promise.reject(
			new Error(typeof t === "function" && t("signin-signup.weakPassword"))
		);
	};
}
